import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Armchair, Plus, Users, CheckCircle2, XCircle, Calendar, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  useToast, useConfirm,
  EmptyState, PageHeader, StatusBadge, Button, Input, FilterTabs,
} from '../components/ui';

const tableStatusVariant = (status) => {
  switch (status) {
    case 'available': return 'success';
    case 'occupied':  return 'danger';
    case 'reserved':  return 'warning';
    case 'cleaning':  return 'primary';
    default:          return 'neutral';
  }
};

// Visual config for the floor-plan table tiles
const TILE_STYLES = {
  available: 'bg-success-100 border-success-500 text-success-900',
  occupied:  'bg-danger-100 border-danger-500 text-danger-900',
  reserved:  'bg-warning-100 border-warning-500 text-warning-900',
  cleaning:  'bg-primary-100 border-primary-500 text-primary-900',
};

const STAT_DEFS = [
  { key: 'total',     label: 'Total tables',  color: 'primary', Icon: Armchair    },
  { key: 'available', label: 'Available',     color: 'success', Icon: CheckCircle2 },
  { key: 'occupied',  label: 'Occupied',      color: 'danger',  Icon: Users        },
  { key: 'reserved',  label: 'Reserved',      color: 'warning', Icon: Calendar     },
];

const TINT = {
  primary: 'bg-primary-50 text-primary-600',
  success: 'bg-success-50 text-success-600',
  warning: 'bg-warning-50 text-warning-600',
  danger:  'bg-danger-50 text-danger-600',
};

const Tables = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const confirm = useConfirm();
  const [tables, setTables] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [filter, setFilter] = useState('all');
  const [formData, setFormData] = useState({
    number: '',
    capacity: 4,
    location: 'indoor',
    floor: 'main',
    shape: 'round',
    width: 80,
    height: 80,
    notes: ''
  });

  const canEdit = ['super_admin', 'admin', 'manager'].includes(user?.role);

  // --- Floor-plan layout & drag-to-reposition ---
  const floorRef = useRef(null);
  const [floorWidth, setFloorWidth] = useState(0);
  const [dragPos, setDragPos] = useState(null); // live position of the tile being dragged
  const dragRef = useRef(null);
  const suppressClick = useRef(false); // stops a drag from also firing the edit click

  const SLOT = 110;   // grid cell size in px (tile + gap)
  const MARGIN = 20;
  const perRow = Math.max(1, Math.floor((floorWidth - MARGIN) / SLOT));
  const rows = Math.max(1, Math.ceil(tables.length / perRow));
  const floorHeight = Math.max(400, rows * SLOT + MARGIN * 2);

  // Auto grid slot for a table that has never been positioned
  const gridPos = (index) => ({
    x: MARGIN + (index % perRow) * SLOT,
    y: MARGIN + Math.floor(index / perRow) * SLOT,
  });

  // Where a tile should render: live drag > saved position > auto grid.
  // Saved positions are clamped to the current floor width so a table
  // dragged into place on a wide desktop screen doesn't render off-screen
  // (and get invisibly clipped) when the same page is opened on a phone.
  const effectivePos = (table, index) => {
    if (dragPos && dragPos.id === table._id) return { x: dragPos.x, y: dragPos.y };
    if (table.x || table.y) {
      const w = table.width || 80;
      const maxX = Math.max(MARGIN, floorWidth - w - MARGIN);
      return {
        x: Math.min(Math.max(table.x || 0, 0), maxX),
        y: Math.max(table.y || 0, 0),
      };
    }
    return gridPos(index);
  };

  useEffect(() => {
    const measure = () => setFloorWidth(floorRef.current?.clientWidth || 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const startDrag = (e, table, index) => {
    if (!canEdit) return;
    suppressClick.current = false; // clear any stale flag from a prior drag
    const rect = floorRef.current.getBoundingClientRect();
    const p = effectivePos(table, index);
    dragRef.current = {
      id: table._id,
      offsetX: e.clientX - rect.left - p.x,
      offsetY: e.clientY - rect.top - p.y,
      x: p.x, y: p.y, moved: false,
    };
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', endDrag);
  };

  const onDragMove = (e) => {
    const d = dragRef.current;
    if (!d || !floorRef.current) return;
    const rect = floorRef.current.getBoundingClientRect();
    let x = e.clientX - rect.left - d.offsetX;
    let y = e.clientY - rect.top - d.offsetY;
    x = Math.max(0, Math.min(x, rect.width - 80));
    y = Math.max(0, y);
    d.x = x; d.y = y; d.moved = true;
    setDragPos({ id: d.id, x, y });
  };

  const endDrag = async () => {
    window.removeEventListener('mousemove', onDragMove);
    window.removeEventListener('mouseup', endDrag);
    const d = dragRef.current;
    dragRef.current = null;
    if (d && d.moved) {
      suppressClick.current = true; // this "click" was really a drag
      const x = Math.round(d.x), y = Math.round(d.y);
      setTables(prev => prev.map(t => (t._id === d.id ? { ...t, x, y } : t)));
      setDragPos(null);
      try {
        await axios.put(`/api/tables/${d.id}`, { x, y }, {
          headers: { 'x-auth-token': localStorage.getItem('token') },
        });
      } catch (err) {
        toast.error('Could not save table position');
      }
    } else {
      setDragPos(null);
    }
  };

  useEffect(() => {
    fetchTables();
  }, [filter]);

  const fetchTables = async () => {
    try {
      const query = filter !== 'all' ? `?status=${filter}` : '';
      const res = await axios.get(`/api/tables${query}`, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setTables(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTable) {
        await axios.put(`/api/tables/${editingTable._id}`, formData, {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        });
      } else {
        await axios.post('/api/tables', formData, {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        });
      }
      setShowModal(false);
      setEditingTable(null);
      setFormData({
        number: '', capacity: 4, location: 'indoor', floor: 'main',
        shape: 'round', width: 80, height: 80, notes: ''
      });
      fetchTables();
      toast.success(editingTable ? 'Table updated' : 'Table created');
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error saving table');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`/api/tables/${id}/status`, { status }, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      fetchTables();
    } catch (err) {
      toast.error('Error updating status');
    }
  };

  const deleteTable = async (id) => {
    const ok = await confirm({
      title: 'Delete table?',
      message: 'This will permanently remove the table.',
      variant: 'danger',
      confirmText: 'Delete'
    });
    if (!ok) return;
    try {
      await axios.delete(`/api/tables/${id}`, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      toast.success('Table deleted');
      fetchTables();
    } catch (err) {
      toast.error('Error deleting table');
    }
  };

  const openEditModal = (table) => {
    if (!canEdit) return;
    // The table list is already fresh from the server — use it directly
    // instead of a redundant (and previously broken) per-id fetch.
    setEditingTable(table);
    setFormData({
      number: table.number,
      capacity: table.capacity,
      location: table.location,
      floor: table.floor,
      shape: table.shape,
      width: table.width,
      height: table.height,
      notes: table.notes || ''
    });
    setShowModal(true);
  };

  const statValue = (key) => {
    if (key === 'total') return tables.length;
    return tables.filter(t => t.status === key).length;
  };

  return (
    <>
      <PageHeader
        title="Tables"
        subtitle="Manage seating, status, and floor layout."
        action={canEdit && (
          <Button
            variant="primary"
            leftIcon={<Plus size={16} />}
            onClick={() => {
              setEditingTable(null);
              setFormData({
                number: '', capacity: 4, location: 'indoor', floor: 'main',
                shape: 'round', width: 80, height: 80, notes: ''
              });
              setShowModal(true);
            }}
          >
            Add Table
          </Button>
        )}
      />

      {/* Filter Tabs */}
      <div className="mb-6">
        <FilterTabs
          value={filter}
          onChange={setFilter}
          options={['all', 'available', 'occupied', 'reserved', 'cleaning']}
        />
      </div>

      {/* Stats — same pattern as Dashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {STAT_DEFS.map(({ key, label, color, Icon }) => (
          <div key={key} className="bg-white rounded-lg border border-neutral-200 shadow-card p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</p>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${TINT[color]}`}>
                <Icon size={18} />
              </div>
            </div>
            <p className="text-3xl font-bold text-neutral-900 tabular-nums">{statValue(key)}</p>
          </div>
        ))}
      </div>

      {/* Visual Floor Plan */}
      <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden mb-6">
        <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-neutral-800">Floor plan</h2>
          {canEdit && (
            <p className="text-xs text-neutral-500">Click a table to edit · drag to reposition</p>
          )}
        </div>
        <div
          ref={floorRef}
          className="relative"
          style={{
            width: '100%',
            height: `${floorHeight}px`,
            backgroundImage: 'radial-gradient(circle at 1px 1px, #e2e8f0 1px, transparent 0)',
            backgroundSize: '20px 20px',
            backgroundColor: '#f8fafc',
          }}
        >
          {tables.map((table, index) => {
            const tint = TILE_STYLES[table.status] || 'bg-neutral-100 border-neutral-300 text-neutral-700';
            const pos = effectivePos(table, index);
            const isDragging = dragPos && dragPos.id === table._id;
            return (
              <button
                key={table._id}
                onMouseDown={(e) => startDrag(e, table, index)}
                onClick={() => {
                  if (suppressClick.current) { suppressClick.current = false; return; }
                  openEditModal(table);
                }}
                disabled={!canEdit}
                className={`
                  absolute border-2 flex flex-col items-center justify-center font-semibold select-none
                  ${isDragging ? 'shadow-card-hover z-10' : 'transition-all'}
                  ${tint}
                  ${canEdit ? 'cursor-move hover:shadow-card-hover' : 'cursor-default'}
                `}
                style={{
                  left: `${pos.x}px`,
                  top: `${pos.y}px`,
                  width: `${table.width || 80}px`,
                  height: `${table.height || 80}px`,
                  borderRadius:
                    table.shape === 'round' ? '50%' :
                    table.shape === 'square' ? '8px' : '4px',
                }}
                title={`Table ${table.number} · ${table.status}`}
              >
                <span className="text-base font-bold leading-none">#{table.number}</span>
                <span className="text-[10px] mt-0.5 opacity-80">{table.capacity} seats</span>
                {table.handledBy?.name && table.status !== 'available' && (
                  <span className="text-[9px] mt-0.5 opacity-90 truncate max-w-full px-1" title={table.handledBy.name}>
                    {table.handledBy.name.split(' ')[0]}
                  </span>
                )}
                {table.status === 'occupied' && (
                  <span className="text-[9px] font-bold uppercase mt-0.5 tracking-wider">Busy</span>
                )}
                {table.status === 'reserved' && (
                  <span className="text-[9px] font-bold uppercase mt-1 tracking-wider">Res</span>
                )}
              </button>
            );
          })}
          {tables.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-neutral-400">
              No tables to display
            </div>
          )}
        </div>
      </div>

      {/* Table List */}
      <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-auto">
        <table className="app-table">
          <thead>
            <tr>
              <th>Table #</th>
              <th>Capacity</th>
              <th>Status</th>
              <th>Location</th>
              <th>Handled by</th>
              <th>Occupied Time</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tables.length === 0 && (
              <tr>
                <td colSpan="7">
                  <EmptyState
                    icon={Armchair}
                    title={filter === 'all' ? 'No tables yet' : `No ${filter} tables`}
                    message={filter === 'all'
                      ? 'Add your first table to start managing seating.'
                      : 'Try a different filter above.'}
                  />
                </td>
              </tr>
            )}
            {tables.map(table => (
              <tr key={table._id}>
                <td className="font-semibold">#{table.number}</td>
                <td>{table.capacity} seats</td>
                <td>
                  <StatusBadge variant={tableStatusVariant(table.status)} dot>
                    {table.status}
                  </StatusBadge>
                </td>
                <td className="capitalize">{table.location}</td>
                <td className="text-sm">
                  {table.handledBy?.name
                    ? <span className="text-neutral-800">{table.handledBy.name}</span>
                    : <span className="text-neutral-400">—</span>}
                </td>
                <td className="text-sm text-neutral-600">
                  {table.occupiedSince
                    ? `${Math.floor((new Date() - new Date(table.occupiedSince)) / 60000)} mins`
                    : '-'}
                </td>
                <td>
                  <div className="flex flex-wrap gap-2">
                    {table.status === 'available' && (
                      <Button size="sm" variant="danger" onClick={() => updateStatus(table._id, 'occupied')}>
                        Occupy
                      </Button>
                    )}
                    {table.status === 'occupied' && (
                      <Button size="sm" variant="primary" onClick={() => updateStatus(table._id, 'cleaning')}>
                        Finish
                      </Button>
                    )}
                    {table.status === 'cleaning' && (
                      <Button size="sm" variant="success" onClick={() => updateStatus(table._id, 'available')}>
                        Mark ready
                      </Button>
                    )}
                    {table.status === 'reserved' && (
                      <Button size="sm" variant="danger" onClick={() => updateStatus(table._id, 'occupied')}>
                        Seat
                      </Button>
                    )}
                    {canEdit && (
                      <Button size="sm" variant="secondary" onClick={() => openEditModal(table)}>
                        Edit
                      </Button>
                    )}
                    {canEdit && (
                      <Button size="sm" variant="ghost" onClick={() => deleteTable(table._id)}>
                        Delete
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-card-hover w-full max-w-md">
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-neutral-900">
                {editingTable ? 'Edit table' : 'Add new table'}
              </h2>
              <button
                type="button"
                onClick={() => { setShowModal(false); setEditingTable(null); }}
                className="text-neutral-400 hover:text-neutral-700 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-4">
                <Input
                  label="Table number"
                  type="number"
                  value={formData.number}
                  onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                  required
                />
                <Input
                  label="Capacity (seats)"
                  type="number"
                  min="1"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  required
                />
                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-1">Location</label>
                  <select
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
                  >
                    <option value="indoor">Indoor</option>
                    <option value="outdoor">Outdoor</option>
                    <option value="bar">Bar</option>
                    <option value="private">Private</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-2">Shape</label>
                  <FilterTabs
                    value={formData.shape}
                    onChange={(shape) => setFormData({ ...formData, shape })}
                    options={['round', 'square', 'rectangle']}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-1">Notes (optional)</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="2"
                    className="w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500 resize-none"
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-lg flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  fullWidth
                  onClick={() => { setShowModal(false); setEditingTable(null); }}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" fullWidth>
                  {editingTable ? 'Update table' : 'Create table'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Tables;
